import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon, ArrowRightIcon } from 'lucide-react';
type Answer = {
  emoji: string;
  label: string;
  score: number;
};
type Question = {
  id: number;
  text: string;
  answers: Answer[];
};
const questions: Question[] = [{
  id: 1,
  text: 'Antes del partido, tu hijo suele estar…',
  answers: [{
    emoji: '😟',
    label: 'Muy tenso',
    score: 1
  }, {
    emoji: '😕',
    label: 'Con dudas',
    score: 2
  }, {
    emoji: '🙂',
    label: 'Bastante tranquilo',
    score: 3
  }, {
    emoji: '😄',
    label: 'Seguro y motivado',
    score: 4
  }]
}, {
  id: 2,
  text: 'Cuando se equivoca, normalmente…',
  answers: [{
    emoji: '😟',
    label: 'Se viene abajo enseguida',
    score: 1
  }, {
    emoji: '😕',
    label: 'Le cuesta recuperarse',
    score: 2
  }, {
    emoji: '🙂',
    label: 'Se recompone poco a poco',
    score: 3
  }, {
    emoji: '😄',
    label: 'Sigue compitiendo con normalidad',
    score: 4
  }]
}, {
  id: 3,
  text: 'Cuando siente presión, suele…',
  answers: [{
    emoji: '😟',
    label: 'Bloquearse',
    score: 1
  }, {
    emoji: '😕',
    label: 'Jugar peor',
    score: 2
  }, {
    emoji: '🙂',
    label: 'Mantenerse a medias',
    score: 3
  }, {
    emoji: '😄',
    label: 'Responder bastante bien',
    score: 4
  }]
}, {
  id: 4,
  text: 'En los partidos, su emoción más habitual es…',
  answers: [{
    emoji: '😟',
    label: 'Miedo',
    score: 1
  }, {
    emoji: '😕',
    label: 'Inseguridad',
    score: 2
  }, {
    emoji: '🙂',
    label: 'Mezcla de tensión y ganas',
    score: 3
  }, {
    emoji: '😄',
    label: 'Confianza y disfrute',
    score: 4
  }]
}];
type ResultState = {
  title: string;
  body: string;
  cta: string;
  colorClass: string;
  bgClass: string;
  emoji: string;
};
const getResult = (score: number): ResultState => {
  if (score <= 7) {
    return {
      emoji: '😟',
      title: 'Ahora mismo está compitiendo bloqueado',
      body: 'Tu hijo probablemente está viviendo los partidos con demasiada tensión, miedo al error o inseguridad. Si hoy le ves apagarse, bloquearse o venirse abajo, este es un buen momento para ayudarle a recuperar confianza.',
      cta: 'Quiero ayudarle ahora',
      colorClass: 'text-brand-slate',
      bgClass: 'bg-[#f8f9fa] border-gray-100'
    };
  }
  if (score <= 10) {
    return {
      emoji: '😕',
      title: 'Ahora mismo está compitiendo con inseguridad',
      body: 'Hay señales claras de que la presión, los errores o el contexto del partido le están afectando más de lo que deberían. Reforzar su confianza ahora puede marcar una gran diferencia.',
      cta: 'Quiero reforzar su confianza',
      colorClass: 'text-brand-slate',
      bgClass: 'bg-[#f8f9fa] border-gray-100'
    };
  }
  if (score <= 13) {
    return {
      emoji: '🙂',
      title: 'Está en desarrollo emocional',
      body: 'Tu hijo ya muestra algunos recursos positivos, pero todavía necesita ganar estabilidad para competir con más calma, seguridad y mejor respuesta ante los errores.',
      cta: 'Quiero seguir ayudándole',
      colorClass: 'text-brand-slate',
      bgClass: 'bg-[#f8f9fa] border-gray-100'
    };
  }
  return {
    emoji: '😄',
    title: 'Tiene una buena base emocional',
    body: 'Tu hijo parece competir con una base bastante sana, aunque seguir reforzando su confianza puede ayudarle a sostenerla incluso en partidos más exigentes.',
    cta: 'Quiero fortalecer su mentalidad',
    colorClass: 'text-brand-slate',
    bgClass: 'bg-[#f8f9fa] border-gray-100'
  };
};
export function EmotionalQuiz() {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [scores, setScores] = useState<number[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const handleAnswer = (score: number) => {
    const newScores = [...scores, score];
    setScores(newScores);
    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 300);
    } else {
      setTimeout(() => {
        setIsFinished(true);
      }, 400);
    }
  };
  const handleCTAClick = () => {
    const offerSection = document.getElementById('offer');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    if (isFinished) {
      const timer = setTimeout(() => {
        handleCTAClick();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [isFinished]);
  const totalScore = scores.reduce((a, b) => a + b, 0);
  const result = isFinished ? getResult(totalScore) : null;
  return <section className="py-20 md:py-28 relative bg-[#f8f9fa]" data-id="element-29">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-30">
        <div className="text-center mb-12 md:mb-16" data-id="element-31">
          <p className="tracking-wide uppercase mb-4 text-brand-amber text-[16px] font-[800]" data-id="element-32">
            Medidor emocional competitivo
          </p>
          <h2 className="md:text-4xl font-heading text-brand-slate mb-4 text-[37px]" data-id="element-33">
            Descubre en 20 segundos cómo está tu hijo al competir
          </h2>
          <p className="text-brand-slate-light md:text-lg text-[14px]" data-id="element-34">
            Responde 4 preguntas rápidas y descubre si ahora mismo compite
            bloqueado, con inseguridad o con una buena base emocional.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden relative min-h-[400px] flex flex-col" data-id="element-35">
          <AnimatePresence mode="wait" data-id="element-36">
            {!isFinished ? <motion.div key="quiz" initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.3
          }} className="p-6 md:p-8 flex-grow flex flex-col" data-id="element-37">
                <div className="mb-8" data-id="element-38">
                  <div className="flex justify-between text-xs font-medium text-brand-slate-muted uppercase tracking-wide mb-3" data-id="element-39">
                    <span data-id="element-40">
                      Pregunta {currentQuestionIndex + 1} de {questions.length}
                    </span>
                    <span data-id="element-41">
                      {Math.round(currentQuestionIndex / questions.length * 100)}
                      %
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-[#f8f9fa] rounded-full overflow-hidden" data-id="element-42">
                    <motion.div className="h-full bg-brand-slate" initial={{
                  width: `${currentQuestionIndex / questions.length * 100}%`
                }} animate={{
                  width: `${(currentQuestionIndex + 1) / questions.length * 100}%`
                }} transition={{
                  duration: 0.4,
                  ease: 'easeOut'
                }} data-id="element-43" />
                  </div>
                </div>

                <div className="mb-8" data-id="element-44">
                  <h3 className="text-xl md:text-2xl font-medium text-brand-slate leading-tight" data-id="element-45">
                    {questions[currentQuestionIndex].text}
                  </h3>
                </div>

                <div className="space-y-3 mt-auto" data-id="element-46">
                  {questions[currentQuestionIndex].answers.map((answer, idx) => <button key={idx} onClick={() => handleAnswer(answer.score)} className="w-full text-left p-4 md:p-5 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-[#f8f9fa] transition-colors duration-200 flex items-center group" data-id="element-47">
                        <span className="text-2xl mr-4" data-id="element-48">{answer.emoji}</span>
                        <span className="text-base md:text-lg text-brand-slate group-hover:text-brand-slate transition-colors" data-id="element-49">
                          {answer.label}
                        </span>
                        <ChevronRightIcon className="w-5 h-5 ml-auto text-gray-300 group-hover:text-brand-slate transition-colors" data-id="element-50" />
                      </button>)}
                </div>
              </motion.div> : <motion.div key="result" initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="p-8 md:p-12 flex-grow flex flex-col items-center text-center" data-id="element-51">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-6 border ${result?.bgClass}`} data-id="element-52">
                  {result?.emoji}
                </div>

                <h3 className={`text-2xl md:text-3xl font-heading mb-4 ${result?.colorClass}`} data-id="element-53">
                  {result?.title}
                </h3>

                <p className="text-base md:text-lg text-brand-slate-light mb-10 leading-relaxed" data-id="element-54">
                  {result?.body}
                </p>

                <button onClick={handleCTAClick} className="w-full sm:w-auto flex items-center justify-center px-8 py-4 bg-brand-amber text-white rounded-xl font-semibold text-base md:text-lg hover:bg-brand-amber-dark transition-colors duration-200 group" data-id="element-55">
                  {result?.cta}
                  <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-56" />
                </button>

                <p className="mt-8 text-sm text-brand-slate-muted italic" data-id="element-57">
                  “Competir Sin Miedo es una guía práctica para ayudar a tu hijo
                  a gestionar mejor la presión, recuperarse del error y volver a
                  jugar con confianza.”
                </p>
              </motion.div>}
          </AnimatePresence>
        </div>

        <p className="text-center text-xs text-brand-slate-muted mt-6" data-id="element-58">
          Este medidor es orientativo y no sustituye una valoración profesional
          individual.
        </p>
      </div>
    </section>;
}